/**
 * The Following view's right column (§3.3): what it shows depends on what you follow (the snapshot
 * carries the panel that fits: "Your legislation" 07, the person card 08, "Your topics" + "Alert me
 * when" 09), then the weekly digest setting and the mini calendar, for everyone.
 */
import { useState } from "react";
import { ArrowRight, BellRing, ChevronLeft, ChevronRight, Mail, Play, Plus } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { followingStore, useFollowing } from "../../stores/followingStore";
import { TypeBadge } from "./TypeBadge";
import { List, ListRow, RowActions, RowLead, RowMain, RowSub, RowTitle } from "../shared/List";
import { clock } from "../../lib/format";
import type { FollowingSettings, MeetingSummary } from "../../types";

const STEPS = ["Filed", "Introduced", "Committee", "Council", "Final"];

function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className="switch switch--md" onClick={() => onChange(!on)} />;
}

function YourLegislation({ onTrack }: { onTrack: () => void }) {
  const bills = useFollowing((s) => s.legislation);
  return (
    <section className="side-list">
      <div className="side-head"><h2>Your legislation</h2><span>{bills.length} tracked</span></div>
      <List className="bill-tracks">
        {bills.map((b) => (
          <ListRow key={b.id} className="bill-track" href={`/dashboard/legislation/${encodeURIComponent(b.id)}`}>
            <RowMain min={0} className="bill-track__main">
              <span className="bill-track__head"><RowTitle className="bill-track__name">{b.label}</RowTitle><span className="bill-track__id">{b.id}</span></span>
              <span className="bill-progress" role="img" aria-label={`${STEPS[b.stage]}${b.moving ? " next" : ""}`}>
                {STEPS.map((step, i) => <i key={step} data-state={i < b.stage ? "done" : i === b.stage ? (b.moving ? "next" : "current") : "todo"} />)}
              </span>
              <span className="bill-track__status">{b.status}</span>
              <span className="bill-track__next" data-moving={b.moving || undefined}><ArrowRight size={12} />{b.next}</span>
            </RowMain>
          </ListRow>
        ))}
      </List>
      <button className="side-add" onClick={onTrack}><Plus size={14} />Track another ordinance</button>
    </section>
  );
}

function PersonCardPanel() {
  const { card, people } = useFollowing(useShallow((s) => ({ card: s.person, people: s.people })));
  if (!card) return null;
  const p = people[card.personId];
  if (!p) return null;
  const name = `${p.title ? `${p.title} ` : ""}${p.name}`;
  return (
    <section className="person-card">
      <List as="div" variant="plain" className="person-card__head">
        <ListRow as="div" className="person-card__profile" wrap={false}>
          <RowLead><span className="person-card__avatar" style={p.photoUrl ? { backgroundImage: `url(${p.photoUrl})` } : undefined} role="img" aria-label={p.name} /></RowLead>
          <RowMain min={0} className="person-card__text">
            <RowTitle className="person-card__name" lines={2}>{name}</RowTitle>
            <RowSub className="person-card__ward">{[p.ward != null ? `Ward ${p.ward}` : null, `since ${card.since}`].filter(Boolean).join(" · ")}</RowSub>
          </RowMain>
        </ListRow>
      </List>
      <div className="person-card__stats">
        <span><b>{card.meetingsSpokenAt}</b>{card.meetingsSpokenAt === 1 ? "Meeting spoken at" : "Meetings spoken at"}</span>
        <span><b>{card.itemsSponsored}</b>{card.itemsSponsored === 1 ? "Item sponsored" : "Items sponsored"}</span>
        <span><b>{card.colleagueMentions}</b>{card.colleagueMentions === 1 ? "Colleague mention" : "Colleague mentions"}</span>
      </div>
      {card.quote && (
        <>
          <span className="person-card__quote-label">Latest quote</span>
          <p className="person-card__quote">“{card.quote.text}”</p>
          <a className="person-card__jump" href={`/dashboard/meetings/${encodeURIComponent(card.quote.meetingId)}?t=${clock(card.quote.at)}`}>
            <Play size={11} />{card.quote.where} · {clock(card.quote.at)}
          </a>
        </>
      )}
      <a className="person-card__open" href={`/dashboard/people/${encodeURIComponent(card.personId)}`}>Full voting &amp; speaking record<ArrowRight size={14} /></a>
    </section>
  );
}

function YourTopics() {
  const { topics, aside, settings } = useFollowing(useShallow((s) => ({ topics: s.topics, aside: s.topicsAside, settings: s.settings })));
  const set = (k: keyof FollowingSettings["alerts"], v: boolean) => void followingStore.getState().saveSettings({ ...settings, alerts: { ...settings.alerts, [k]: v } });
  const rows: [keyof FollowingSettings["alerts"], string][] = [
    ["agendaListsTopics", "An agenda lists my topics"],
    ["topicsInTranscript", "My topics come up in a transcript"],
    ["referredToCommittees", "Legislation is referred to my committees"],
  ];
  return (
    <section className="side-list">
      <div className="side-head"><h2>Your topics</h2>{aside && <span>{aside}</span>}</div>
      <List className="topic-statuses">
        {topics.map((t) => (
          <ListRow key={t.followId} className="topic-status">
            <RowLead><TypeBadge type="topic" /></RowLead>
            <RowMain min={0} className="topic-status__text">
              <RowTitle className="topic-status__name">{t.label}</RowTitle>
              <RowSub wrap className="topic-status__latest">{t.latest}</RowSub>
              <RowSub wrap className="topic-status__where">{t.where}</RowSub>
            </RowMain>
          </ListRow>
        ))}
      </List>
      <div className="alerts">
        <span className="alerts__head"><BellRing size={14} />Alert me when</span>
        <List variant="plain" className="alerts__list">
          {rows.map(([k, label]) => (
            <ListRow key={k} className="alerts__row" wrap={false}>
              <RowMain min={0}><RowSub wrap className="alerts__label">{label}</RowSub></RowMain>
              <RowActions><Switch on={settings.alerts[k]} label={label} onChange={(v) => set(k, v)} /></RowActions>
            </ListRow>
          ))}
        </List>
      </div>
    </section>
  );
}

function WeeklyDigest() {
  const settings = useFollowing((s) => s.settings);
  return (
    <section className="digest">
      <span className="digest__icon"><Mail size={15} /></span>
      <span className="digest__text"><span className="digest__title">Weekly digest</span><span className="digest__sub">Mondays, only when something moves</span></span>
      <Switch on={settings.digest} label="Weekly digest" onChange={(v) => void followingStore.getState().saveSettings({ ...settings, digest: v })} />
    </section>
  );
}

/** Chicago calendar date (YYYY-MM-DD) of an instant. */
const ymd = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

export function MiniCalendar({ meetings, followedUpcoming, onPick }: { meetings: MeetingSummary[]; followedUpcoming: Set<string>; onPick: (day: string) => void }) {
  const today = ymd(new Date());
  const [month, setMonth] = useState(() => { const [y, m] = today.split("-").map(Number); return { y, m: m - 1 }; });
  const withMeetings = new Set(meetings.map((m) => ymd(new Date(m.startsAt))));
  const first = new Date(Date.UTC(month.y, month.m, 1));
  const days = new Date(Date.UTC(month.y, month.m + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [...Array(first.getUTCDay()).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const key = (d: number) => `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const shift = (n: number) => setMonth(({ y, m }) => ({ y: y + Math.floor((m + n) / 12), m: (((m + n) % 12) + 12) % 12 }));
  return (
    <section className="mini-cal">
      <div className="mini-cal__head">
        <span>{first.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" })}</span>
        <span className="mini-cal__nav">
          <button aria-label="Previous month" onClick={() => shift(-1)}><ChevronLeft size={16} /></button>
          <button aria-label="Next month" onClick={() => shift(1)}><ChevronRight size={16} /></button>
        </span>
      </div>
      <div className="mini-cal__grid" role="group" aria-label={first.toLocaleDateString("en-US", { timeZone: "UTC", month: "long", year: "numeric" })}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} className="mini-cal__wd" aria-hidden>{d}</span>)}
        {cells.map((d, i) => {
          if (d == null) return <span key={i} className="mini-cal__cell" />;
          const k = key(d);
          const state = k === today ? "today" : followedUpcoming.has(k) ? "followed" : k < today ? "past" : "future";
          return (
            <span key={i} className="mini-cal__cell">
              <button className="mini-cal__day" data-state={state} onClick={() => onPick(k)} disabled={!withMeetings.has(k)}
                aria-label={new Date(`${k}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "long", month: "long", day: "numeric" })}>{d}</button>
              {withMeetings.has(k) && (state === "past" || state === "future") && <i className="mini-cal__dot" />}
            </span>
          );
        })}
      </div>
      <div className="mini-cal__legend"><span><i className="mini-cal__dot" />Meetings</span><span><i className="mini-cal__ring" />Upcoming</span></div>
    </section>
  );
}

export function FollowingSide({ followedUpcoming, onPickDay, onTrack }: { followedUpcoming: Set<string>; onPickDay: (day: string) => void; onTrack: () => void }) {
  const { hasBills, hasPerson, hasTopics, meetings } = useFollowing(useShallow((s) => ({
    hasBills: s.legislation.length > 0, hasPerson: !!s.person, hasTopics: s.topics.length > 0, meetings: s.meetings,
  })));
  return (
    <aside className="following-side">
      {hasPerson ? <PersonCardPanel /> : hasBills ? <YourLegislation onTrack={onTrack} /> : hasTopics ? <YourTopics /> : null}
      <WeeklyDigest />
      <MiniCalendar meetings={meetings} followedUpcoming={followedUpcoming} onPick={onPickDay} />
    </aside>
  );
}
